import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { apiAddress } from "../../app/apiModel";
import { CompletePasswordResetRequest, AccountActivation, ErrorResponse, GetLoginResponse, LogInRequest, InitPasswordReset, } from "../Authentification/authModel";
import { handleFulfilled, handlePending, handleRejected, RejectedValue, WebShopBimRefreshToken, WebShopBimTAccessToken, } from "../../app/common";
import jwtDecode from "jwt-decode";

// SSO acquires the Microsoft token in the component that owns the MSAL instance
// (features/SSO/signInSSOButton.tsx) and exchanges it for centralization tokens via
// getTokenFromSSO below. From that point on only the centralization tokens are used, so
// there is no Microsoft token left to refresh.

export const getTokenFromSSO = createAsyncThunk<GetLoginResponse, void, { rejectValue: RejectedValue }>(
  "AuthentificationSlice/TokenFromSSO",
  async (_, { rejectWithValue }) => {

    const accessToken = localStorage.getItem(WebShopBimTAccessToken);
    const response = await fetch(apiAddress + "auth/ssotoken", {
      headers: {
        Authorization: 'Bearer ' + accessToken,
      },
    });
    if (response.status === 200) {
      const logInResponse = (await response.json()) as GetLoginResponse;
      localStorage.clear();
      localStorage.setItem(WebShopBimTAccessToken, logInResponse.accessToken);
      localStorage.setItem(WebShopBimRefreshToken, logInResponse.refreshToken);

      return logInResponse;
    }
    else {
      const json = await response.json();
      if (typeof json === "string") {
        return rejectWithValue({ message: json, status: response.status });
      }
      const errorResponse = (await response.json()) as ErrorResponse;

      return rejectWithValue({ message: errorResponse.detail ?? response.statusText, status: response.status });
    }
  }
);

export const postActivateAccount = createAsyncThunk<string, AccountActivation, { rejectValue: RejectedValue }>(
  "AuthentificationSlice/verifyaccount",
  async (user: AccountActivation, { rejectWithValue }) => {
    const requestHeaders: HeadersInit = new Headers();
    requestHeaders.set('Content-Type', 'application/json');
    const response = await fetch(apiAddress + "auth/verifyaccount", {
      method: "POST",
      headers: requestHeaders,
      body: JSON.stringify({
        username: user.username,
        code: user.code,
      }),
    });

    if (response.status === 200) {
      return response.status.toString();
    }
    else if (response.status === 401 || response.status === 402) {
      return rejectWithValue({ message: response.statusText, status: response.status })
    }
    else {
      const errorResponse = (await response.json()) as ErrorResponse;
      return rejectWithValue({
        message: errorResponse.detail,
        status: response.status,
      });
    }
  }
);

export const postForgotPassword = createAsyncThunk<string, InitPasswordReset, { rejectValue: RejectedValue }>(
  "AuthentificationSlice/ResetPassword",
  async (user: InitPasswordReset, { rejectWithValue }) => {
    const response = await fetch(
      apiAddress + "auth/resetpassword?username=" + user.username,
      {
        method: "POST",
      }
    );

    if (response.status === 200) {
      return response.status.toString();
    }
    else if (response.status === 401 || response.status === 402) {
      return rejectWithValue({ message: response.statusText, status: response.status })
    }
    else {
      const errorResponse = (await response.json()) as ErrorResponse;
      return rejectWithValue({
        message: errorResponse.detail,
        status: response.status,
      });
    }
  }
);

export const postInitActivateAccount = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
  "AuthentificationSlice/InitActivateAccount",
  async (username: string, { rejectWithValue }) => {
    const response = await fetch(
      apiAddress + "auth/initverifyaccount?username=" + username,
      {
        method: "POST",
      }
    );

    if (response.status === 200) {
      return response.status.toString();
    }
    else if (response.status === 401 || response.status === 402) {
      return rejectWithValue({ message: response.statusText, status: response.status })
    }
    else {
      const errorResponse = (await response.json()) as ErrorResponse;
      return rejectWithValue({
        message: errorResponse.detail,
        status: response.status,
      });
    }
  }
);

export const getLogInRequest = createAsyncThunk<GetLoginResponse, LogInRequest, { rejectValue: RejectedValue }>(
  "AuthentificationSlice/Login",
  async (user: LogInRequest, { rejectWithValue }) => {

    const response = await fetch(apiAddress + "auth/signin", {
      headers: {
        Authorization: "Basic " + btoa(user.username + ":" + user.password),
      },
    });
    if (response.status === 200) {
      const logInResponse = (await response.json()) as GetLoginResponse;
      localStorage.setItem(WebShopBimTAccessToken, logInResponse.accessToken);
      localStorage.setItem(WebShopBimRefreshToken, logInResponse.refreshToken);

      const token = localStorage.getItem("webShopBimTAccessToken");
      let itemName: string | undefined;
      if (token !== null) {
        const decodedToken = jwtDecode(token) as { [key: string]: string };

        if (decodedToken) {
          itemName = decodedToken['role'];
        }
      }
      localStorage.setItem("role", itemName!);

      return logInResponse;
    }
    // else if (response.status === 401 || response.status === 402) {
    //   return rejectWithValue({ message: response.statusText, status: response.status })
    // }
    else {
      const errorResponse = (await response.json()) as ErrorResponse;
      return rejectWithValue({ message: errorResponse.detail, status: response.status });
    }
  }
);

// Refreshing lives in app/authFetch.ts, which owns the retry of the request that hit the
// 401. It is deliberately not a thunk: it has to run inside the request path, not beside it.

export const postRegister = createAsyncThunk<string, LogInRequest, { rejectValue: RejectedValue }>(
  "AuthentificationSlice/Register",
  async (user: LogInRequest, { rejectWithValue }) => {
    const response = await fetch(apiAddress + "auth/signup", {
      method: "POST",
      headers: {
        Authorization: "Basic " + btoa(user.username + ":" + user.password),
      },
    });

    if (response.status === 200) {
      return response.status.toString();
    }
    else if (response.status === 401 || response.status === 402) {
      return rejectWithValue({ message: response.statusText, status: response.status })
    }
    else {
      const errorResponse = (await response.json()) as ErrorResponse;
      return rejectWithValue({
        message: errorResponse.detail,
        status: response.status,
      });
    }
  }
);

export const postResetPassword = createAsyncThunk<string, CompletePasswordResetRequest, { rejectValue: RejectedValue }>(
  "AuthentificationSlice/CompleteResetPassword",
  async (user: CompletePasswordResetRequest, { rejectWithValue }) => {
    const requestHeaders: HeadersInit = new Headers();
    requestHeaders.set('Content-Type', 'application/json');
    const response = await fetch(apiAddress + "auth/completeresetpassword", {
      method: "POST",
      headers: requestHeaders,
      body: JSON.stringify({
        username: user.username,
        password: user.password,
        code: user.code,
      }),
    });

    if (response.status === 200) {
      return response.status.toString();
    }
    else if (response.status === 401 || response.status === 402) {
      return rejectWithValue({ message: response.statusText, status: response.status })
    }
    else {
      const errorResponse = (await response.json()) as ErrorResponse;
      return rejectWithValue({
        message: errorResponse.detail,
        status: response.status,
      });
    }
  }
);

export const AuthentificationSlice = createSlice({
  name: "authentificationSlice",
  initialState: {
    statusCode: "",
    isLoading: false,
    error: "",
    hasLoginData: false,
  },
  reducers: {
    resetAuthentificationData: (state) => {
      state.error = "";
      state.isLoading = false;
      state.statusCode = "";
      state.hasLoginData = false;
    },
    setHasLoginData: (state) => {
      state.hasLoginData = true;
    },
    setErrorMessage: (state, action) => {
      state.error = action.payload
    }
  },
  extraReducers(builder) {

    //Activate account
    builder
      .addCase(postActivateAccount.fulfilled, (state, action) => {
        state.statusCode = handleFulfilled(state, action.payload);
      })
      .addCase(postActivateAccount.rejected, (state, action) => {
        handleRejected(state, action.payload ? action.payload : { message: 'Error in application', status: -1 })
      })
      .addCase(postActivateAccount.pending, (state) => {
        handlePending(state);
      });

    //Fortog password
    builder
      .addCase(postForgotPassword.fulfilled, (state, action) => {
        state.statusCode = handleFulfilled(state, action.payload);
      })
      .addCase(postForgotPassword.rejected, (state, action) => {
        handleRejected(state, action.payload ? action.payload : { message: 'Error in application', status: -1 })
      })
      .addCase(postForgotPassword.pending, (state) => {
        handlePending(state);
      });
    //InitActivateAccount
    builder
      .addCase(postInitActivateAccount.fulfilled, (state, action) => {
        state.statusCode = handleFulfilled(state, action.payload);
      })
      .addCase(postInitActivateAccount.rejected, (state, action) => {
        handleRejected(state, action.payload ? action.payload : { message: 'Error in application', status: -1 })
      })
      .addCase(postInitActivateAccount.pending, (state) => {
        handlePending(state);
      });

    //Login
    builder
      .addCase(
        getLogInRequest.fulfilled,
        (state, _action: PayloadAction<GetLoginResponse>) => {
          state.isLoading = false;
          state.hasLoginData = true;
        }
      )
      .addCase(getLogInRequest.pending, (state) => {
        handlePending(state);
      })
      .addCase(getLogInRequest.rejected, (state, action) => {
        handleRejected(state, action.payload ? action.payload : { message: 'Error in application', status: -1 })
      });
    //getTokenFromSSO
    builder
      .addCase(getTokenFromSSO.fulfilled, (state, _action: PayloadAction<GetLoginResponse>) => {
        state.isLoading = false;
        state.hasLoginData = true;
      }
      )
      .addCase(getTokenFromSSO.pending, (state) => {
        handlePending(state);
      })
      .addCase(getTokenFromSSO.rejected, (state, action) => {
        handleRejected(state, action.payload ? action.payload : { message: 'Error in application', status: -1 })
      });

    // .addMatcher((action) => getTokenFromSSO.rejected.match(action) && action.error.message === 'Unauthorized',
    //         (action, state) => {
    //             state.isLoading = false;
    //             state.error = 'Unauthorized';
    // })

    //Register
    builder
      .addCase(postRegister.fulfilled, (state, action) => {
        state.statusCode = handleFulfilled(state, action.payload);
      })
      .addCase(postRegister.rejected, (state, action) => {
        handleRejected(state, action.payload ? action.payload : { message: 'Error in application', status: -1 })
      })
      .addCase(postRegister.pending, (state) => {
        handlePending(state);
      });

    //Reset password
    builder
      .addCase(postResetPassword.fulfilled, (state, action) => {
        state.statusCode = handleFulfilled(state, action.payload);
      })
      .addCase(postResetPassword.rejected, (state, action) => {
        handleRejected(state, action.payload ? action.payload : { message: 'Error in application', status: -1 })
      })
      .addCase(postResetPassword.pending, (state) => {
        handlePending(state);
      });
  },
});

export const { resetAuthentificationData, setHasLoginData, setErrorMessage } =
  AuthentificationSlice.actions;

export default AuthentificationSlice.reducer;
